import React, { useEffect, useState } from 'react';
import pickBy from 'lodash/pickBy';
import omitBy from 'lodash/omitBy';
import isEmpty from 'lodash/isEmpty';
import sortBy from 'lodash/sortBy';
import SelectInput from '../../../../components/presentational/SelectInput';
import Button from '../../../../components/presentational/Button';
import Input from '../../../../components/presentational/Input';
import Typeahead from '../../../Collection/CollectionBlock/Blocks/AgentSelectBlock/Typeahead';
import { useStyles } from '../../../../lib/styles';
import { withQuery } from '../../../../lib/stringUtils';
import { trackEvent } from '../../../../lib/ga';
import { FIND_CRUISE } from '../../lib/ga';
import { InputValidationStatus } from '../../../../components/presentational/Input/InputStatus';
import cruiseLines from './lines.json';
import cruisePlaces from './places.json';
import clsx from 'clsx';
import * as logging from '../../../../server/logging';
import cruiseFormStyles from './CruiseForm_styles';
import Icon from '../../../../components/presentational/Icon';
import axios from 'axios';
import _ from 'lodash';
import { useMountEffect } from '../../../../lib/hooks';
import { Suggestion } from '../../../Collection/CollectionBlock/Blocks/AgentSelectBlock/Typeahead/Typeahead';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
dayjs.extend(duration);

const TRAVELTEK_URL = process.env.TRAVELTEK_URL;

interface Params {
  adults: number;
  departureDate?: string;
  departLocation: {
    portId: string;
    isCountry: boolean;
  };
  arriveLocation: {
    portId: string;
    isCountry: boolean;
  };
  currency: string;
  lineID?: number;
  shipID?: number;
  tripDuration: number;
}

const defaultParams: Params = {
  adults: 2,
  currency: 'AUD',
  tripDuration: 0,
  departLocation: { portId: '', isCountry: false },
  arriveLocation: { portId: '', isCountry: false },
  lineID: undefined,
  departureDate: dayjs()
    .add(dayjs.duration({ months: 1 }))
    .format('YYYY-MM-DD'),
};

const tripDurations = {
  'Any Night': 0,
  '1-4 nights': 1,
  '5-10 nights': 2,
  '11-16 nights': 3,
  '17-31 nights': 4,
  '31+ nights': 5,
};

type Dirty = Partial<Record<keyof Params, boolean>>;
type Validity = Partial<Record<keyof Params, InputValidationStatus>>;

const validators = {
  // lineID: () => true,
  // shipID: () => true,
  // portID: value => !!value,
  departLocation: value => !!value.portId,
  // arriveLocationID: value => !!value,
  // adults: value => parseInt(value, 10) > 0,
  // children: value => parseInt(value, 10) >= 0,
  departureDate: (value: Date) => !!value && new Date(value) >= new Date(),
  // returnDate: (value: Date) => !!value && new Date(value) >= new Date(),
};

const isValid = (validity: Validity) =>
  isEmpty(omitBy(validity, val => val === 'good'));

const validateParams = (params: Params): Validity =>
  Object.keys(validators).reduce(
    (result: Validity, key: string) => ({
      ...result,

      [key]: validators[key](params[key]) ? 'good' : 'bad',
    }),
    {}
  );

const formatDate = (month: string, year: string) =>
  `${year}-${month.padStart(2, '0')}-01`;

const getPortIdsForSearch = (portId: string, returnsCountry = false) => {
  if (!portId) return [];

  //  get the port - could be port or Country
  const port = returnsCountry
    ? cruisePlaces.filter(c => c.countryId === parseInt(portId))[0] //  get country
    : cruisePlaces.filter(c => c.id === parseInt(portId))[0]; //  get port

  const ports = returnsCountry
    ? cruisePlaces.filter(c => c.countryId == port['countryId']).map(c => c.id) //  get all ports for country
    : cruisePlaces
        .filter(
          c =>
            c.name.toLowerCase() == port['name'].toLowerCase() &&
            c.countryId == port['countryId']
        )
        .map(c => c.id); //  get all ports with the same name

  // console.log(ports, `getPortIdsForSearch`);
  return ports;
};

const formatURL = (
  url: string,
  {
    departureDate,
    departLocation,
    arriveLocation,
    lineID,
    shipID,
    tripDuration,
  }: Params
) => {
  //  parameters
  //  /[startdate]/[enddate]/[spreadnights]/[lineid]/[shipid]/[regionid]/[startportid]/[portid]
  //  if there is no value for a parameter, use an underscore
  let totalTravelDays = 0;
  switch (tripDuration) {
    case 1:
      totalTravelDays = 4;
      break;
    case 2:
      totalTravelDays = 10;
      break;
    case 3:
      totalTravelDays = 16;
      break;
    default:
      totalTravelDays = 31;
      break;
  }
  return withQuery(
    url,
    pickBy(
      {
        adults: '2',
        children: '0',
        infants: '0',
        currency: 'AUD',
        startdate: dayjs(departureDate).format('YYYY-MM-DD'),
        enddate: dayjs(departureDate)
          .add(dayjs.duration({ months: 3 }))
          .format('YYYY-MM-DD'),
        formno: '1',
        lineid: lineID,
        portid: getPortIdsForSearch(
          arriveLocation.portId,
          arriveLocation.isCountry
        ).join(','),
        product: 'cruise',
        redirectpageid: '105020',
        spreadnights: tripDuration,
        redirecturl: '/search/cruise/results',
        // regionid: '',
        resultkey: 'default',
        returnurl: location.href,
        shipid: shipID,
        sid: '41200',
        site: 'book.travelat60.com',
        startportid: getPortIdsForSearch(
          departLocation.portId,
          departLocation.isCountry
        ).join(','),
      },
      val => typeof val !== 'undefined'
    )
  );
};

const queryTraveltekForResults = async ({
  departureDate,
  departLocation,
  arriveLocation,
  lineID,
  shipID,
  tripDuration,
}: Params) => {
  return await axios
    .get(
      formatURL(
        `https://fusionapi.traveltek.net/2.0/json/cruisesearchformcounts.pl`,
        {
          departureDate,
          departLocation,
          arriveLocation,
          lineID,
          shipID,
          tripDuration,
          adults: defaultParams.adults,
          currency: defaultParams.currency,
        }
      )
    )
    .then(response => {
      logging.silly(response.data, 'queryTraveltekForResults');
      return response.data;
    })
    .catch(error => console.error('Err: Traveltek search error', error));
};

const search = (params: Params) => {
  // console.log(params, 'Cruise search');
  trackEvent(FIND_CRUISE);
  window.open(formatURL(`${TRAVELTEK_URL}/nx/start.pl`, params));
};

const CruiseForm = () => {
  const [params, setParams] = useState(defaultParams);
  const [dirty, setDirty] = useState<Dirty>({});
  const [validity, setValidity] = useState<Validity>({});
  const [departureLocation, setDepartureLocation] = useState(undefined);
  const [arriveLocation, setArriveLocation] = useState(undefined);
  const styles = useStyles(cruiseFormStyles());
  const [monthModalIsOpen, setMonthModalIsOpen] = React.useState(false);
  const [showResults, setShowResults] = useState('');
  const [countries, setCountries] = useState<any>([]);
  const [departureExpanded, setDepartureExpanded] = useState(false);
  const [arriveExpanded, setArriveExpanded] = useState(false);
  const [favouriteDestinationCountries, setFavouriteDestinationCountries] =
    useState<any>([]);
  const [favouriteDepartureCountries, setFavouriteDepartureCountries] =
    useState<any>([]);
  const [searchResultsFound, setSearchResultsFound] = useState(false);

  useMountEffect(() => {
    //  get unique countries
    const countries = sortBy(
      cruisePlaces.map(c => ({
        id: c.id,
        name: c.name,
        countryId: c.countryId,
        countryName: c.countryName,
      })),
      ['countryId', 'name']
    );
    setCountries(_.uniqBy(countries, 'id'));
    // console.log(countries, 'countries');

    //  do not want to modify the source files supplied by TravelTek, so let's filter them for use in the widget
    // TODO: PLEASE FIX THIS ASAP!
    const favouriteDestinationCountriesCriteria = [
      {
        id: 5,
        name: 'Adelaide',
        countryId: 5,
        countryName: 'Adelaide',
        isCountry: false,
      },
      {
        id: 9,
        name: 'Australia',
        countryId: 9,
        countryName: 'Australia',
        isCountry: true,
      },
      {
        id: 44,
        name: 'Brisbane',
        countryId: 44,
        countryName: 'Brisbane',
        isCountry: false,
      },
      {
        id: 29,
        name: 'Canada',
        countryId: 29,
        countryName: 'Canada',
        isCountry: true,
      },
      {
        id: 59,
        name: 'Fiji',
        countryId: 59,
        countryName: 'Fiji',
        isCountry: true,
      },
      {
        id: 89,
        name: 'Italy',
        countryId: 89,
        countryName: 'Italy',
        isCountry: true,
      },
      {
        id: 92,
        name: 'Japan',
        countryId: 92,
        countryName: 'Japan',
        isCountry: true,
      },
      {
        id: 511,
        name: 'Melbourne',
        countryId: 511,
        countryName: 'Melbourne',
        isCountry: false,
      },
      {
        id: 134,
        name: 'New Caledonia',
        countryId: 134,
        countryName: 'New Caledonia',
        isCountry: true,
      },
      {
        id: 135,
        name: 'New Zealand',
        countryId: 135,
        countryName: 'New Zealand',
        isCountry: true,
      },
      {
        id: 162,
        name: 'Singapore',
        countryId: 162,
        countryName: 'Singapore',
        isCountry: true,
      },
      {
        id: 3328,
        name: 'Sydney',
        countryId: 3328,
        countryName: 'Sydney',
        isCountry: false,
      },
      {
        id: 199,
        name: 'United Kingdom',
        countryId: 199,
        countryName: 'United Kingdom',
        isCountry: true,
      },
      {
        id: 200,
        name: 'United States',
        countryId: 200,
        countryName: 'United States',
        isCountry: true,
      },
    ];

    setFavouriteDestinationCountries(favouriteDestinationCountriesCriteria);
    setFavouriteDepartureCountries(favouriteDestinationCountriesCriteria);
    // console.log(favouriteDestinationCountries, 'favouriteDestinationCountries');

    // not needed for now
    // const favouriteDepartureCountriesCriteria = [9,29,59,63,89,92,134,135,162,169,186,199,200,204,206];
    // setFavouriteDepartureCountries(sortBy(
    //   _.uniqBy(cruisePlaces.filter(country =>
    //     favouriteDepartureCountriesCriteria.includes(country['id'])), 'id'),
    //   'name'));
  });

  const markAllDirty = () =>
    setDirty(
      Object.keys(validators).reduce(
        (result: Dirty, key: string) => ({ ...result, [key]: true }),
        {}
      )
    );

  const onChange = key => value => {
    const newParams = { ...params, [key]: value };
    setParams(newParams);
    setValidity(validateParams(newParams));
    // console.log(newParams, 'Cruise search new params');
    queryForResults(newParams);
  };

  const queryForResults = params => {
    if (Object.values(validateParams(params)).includes('bad')) {
      //  invalid selections
      setShowResults('');
      setSearchResultsFound(false);
      return;
    }

    setShowResults('Checking...');
    queryTraveltekForResults(params)
      .then(d => {
        // console.log(d, 'queryForResults');
        setShowResults(
          d.meta.rows > 0
            ? `${d.meta.rows} result${d.meta.rows > 1 ? 's' : ''}`
            : '0 results'
        );
        setSearchResultsFound(d.meta.rows > 0);
      })
      .catch(e => {
        setShowResults(e.message);
        setSearchResultsFound(false);
        logging.error(e, `CruiseForm: Traveltek re-query error - ${e.message}`);
      });
  };

  const months = [
    { short: 'Jan', full: 'January' },
    { short: 'Feb', full: 'February' },
    { short: 'Mar', full: 'March' },
    { short: 'Apr', full: 'April' },
    { short: 'May', full: 'May' },
    { short: 'Jun', full: 'June' },
    { short: 'Jul', full: 'July' },
    { short: 'Aug', full: 'August' },
    { short: 'Sep', full: 'September' },
    { short: 'Oct', full: 'October' },
    { short: 'Nov', full: 'November' },
    { short: 'Dec', full: 'December' },
  ];

  const years = [
    dayjs().year(),
    dayjs().add(1, 'year').year(),
    dayjs().add(2, 'year').year(),
  ];

  const queryPlaces = search => {
    const filteredPorts = countries.filter(place =>
      place['name'].toLowerCase().includes(search.toLowerCase())
    );

    const filteredCountries = countries.filter(place =>
      place['countryName'].toLowerCase().includes(search.toLowerCase())
    );

    const reducedPorts = _.uniqBy(
      filteredPorts.map(place => ({
        id: place.id,
        name: place.name,
        label: `${place.name}`.replace(
          new RegExp(`${search}`, 'i'),
          str => `<span class='highlight'>${str}</span>`
        ),
        country: place.countryName,
        isCountry: false,
        countryId: place.countryId,
        isExactMatch: place.name.toLowerCase() === search.toLowerCase(),
      })),
      'id'
    );

    const reducedCountries = _.uniqBy(
      filteredCountries.map(place => ({
        id: place.countryId,
        name: place.countryName,
        label: `${place.countryName.replace(
          new RegExp(`${search}`, 'i'),
          str => `<span class='highlight'>${str}</span>`
        )} (all ports)`,
        country: place.countryName,
        isCountry: true,
        countryId: place.countryId,
        isExactMatch: place.countryName.toLowerCase() === search.toLowerCase(),
      })),
      'id'
    );

    const results = _.orderBy(
      reducedPorts.concat(reducedCountries),
      ['isExactMatch', 'name'],
      ['desc', 'asc']
    );
    // console.log(results);
    return results;
  };

  return (
    <div>
      <div className={`cruise-form-fields ${styles.fields}`}>
        <div className={`fields-container ${styles.row}`}>
          <Typeahead
            brand={'tas'}
            className={`${styles.inputContainer} autocomplete-field`}
            inputClassName={styles.input}
            size={'l'}
            value={departureLocation}
            label={'Cruise from'}
            required={true}
            labelClassName={styles.label}
            placeholder={'Departure port or country'}
            setExpanded={departureExpanded}
            status={dirty.departLocation ? validity.departLocation : undefined}
            onChange={(label, value, valid = true, suggestionOptions) => {
              params.departLocation = {
                portId: value,
                isCountry: suggestionOptions.isCountry,
              };
              setDepartureLocation(label);
              setDepartureExpanded(false);
              if (valid) onChange('departLocationID')(value);
              // console.log(`Component onChange Departure`, {label, value, departureExpanded, ...suggestionOptions});
            }}
            onBlur={() => {
              setDirty({ ...dirty, departLocation: true });
              // setDepartureExpanded(false);
              // console.log(`Component onBlur Departure`, {departureExpanded});
            }}
            onSuggest={async (search: string) =>
              queryPlaces(search).map(
                (country): Suggestion => ({
                  label: country['label'],
                  value: country['isCountry']
                    ? country['countryId']
                    : country['id'],
                  suggestionStyles: country['isCountry']
                    ? {}
                    : { fontStyle: 'italic' },
                  name: country['name'],
                  options: {
                    isCountry: country['isCountry'],
                  },
                })
              )
            }
            favourites={favouriteDepartureCountries.map(country => ({
              label: `${country['countryName']}`,
              value: country['countryId'],
              options: {
                isCountry: country['isCountry'],
              },
            }))}
          />

          <Typeahead
            brand={'tas'}
            className={`${styles.inputContainer} autocomplete-field`}
            inputClassName={styles.input}
            size={'l'}
            value={arriveLocation}
            label={'Cruise to'}
            labelClassName={styles.label}
            placeholder={'Any destination'}
            setExpanded={arriveExpanded}
            status={dirty.arriveLocation ? validity.arriveLocation : undefined}
            onChange={(label, value, valid, suggestionOptions) => {
              params.arriveLocation = {
                portId: value,
                isCountry: suggestionOptions.isCountry,
              };
              setArriveLocation(label);
              setArriveExpanded(false);
              if (valid) onChange('arriveLocationID')(value);
              // console.log(`Component onChange Destination`, {label, value, arriveExpanded});
            }}
            onBlur={() => {
              setDirty({ ...dirty, arriveLocation: true });
              // setArriveExpanded(false);
              // console.log(`Component onBlur Destination`, {arriveExpanded});
            }}
            onSuggest={async (search: string) =>
              queryPlaces(search).map(
                (country): Suggestion => ({
                  label: country['label'],
                  value: country['isCountry']
                    ? country['countryId']
                    : country['id'],
                  suggestionStyles: country['isCountry']
                    ? {}
                    : { fontStyle: 'italic' },
                  name: country['name'],
                  options: {
                    isCountry: country['isCountry'],
                  },
                })
              )
            }
            favourites={favouriteDestinationCountries.map(country => ({
              label: `${country['countryName']}`,
              value: country['countryId'],
              options: {
                isCountry: country['isCountry'],
              },
            }))}
          />

          <SelectInput
            containerClassName={`select-input-field ${styles.inputContainer}`}
            className={styles.input}
            placeholder={'Any cruise line'}
            label={'Cruise line'}
            labelClassName={styles.label}
            value={params.lineID}
            options={cruiseLines}
            onChange={onChange('lineID')}
            onBlur={() => setDirty({ ...dirty, lineID: true })}
            status={dirty.lineID ? validity.lineID : undefined}
          />

          <div className="departureDateContainer position-relative">
            <Input
              containerClassName={styles.inputContainer}
              className={styles.input}
              label={'Departing up to 3 months'}
              labelClassName={styles.label}
              value={dayjs(params.departureDate).format('MMMM YYYY')}
              onChange={onChange('departureDate')}
              onBlur={() => setDirty({ ...dirty, departureDate: true })}
              status={dirty.departureDate ? validity.departureDate : undefined}
              onFocus={() => setMonthModalIsOpen(true)}
            />
            <Icon
              icon={'date_range'}
              onClick={() => setMonthModalIsOpen(true)}
            />
          </div>

          <div
            className={clsx('monthSelector', {
              active: monthModalIsOpen,
            })}
          >
            <div className="close" onClick={() => setMonthModalIsOpen(false)}>
              &times;
            </div>
            <div className="monthSelector-meta">
              <h5>Select your month of travel</h5>
              <p>
                Click a month and we will search for cruises departing within 3
                months of that month.
              </p>
            </div>
            <div className="years-container">
              {years.map(year => (
                <div className="yearItem" key={year}>
                  <h5>{year}</h5>
                  <div className="months-container">
                    {months.map((month, key) => {
                      const monthIsNotAvailable =
                        dayjs(
                          `${year}-${key.toString().padStart(2, '0')}`
                        ).format('YYYY-MM') < dayjs().format('YYYY-MM');
                      const isDefaultSelectedMonth =
                        dayjs(`${year}-${month.short}`).format('YYYY-MM') ==
                        dayjs().add(1, 'months').format('YYYY-MM');
                      return (
                        <div
                          className={clsx('monthItem', {
                            disabled: monthIsNotAvailable,
                          })}
                          key={`${month.short}-${year}`}
                          onClick={() => {
                            onChange('departureDate')(
                              formatDate((key + 1).toString(), year.toString())
                            );
                            setMonthModalIsOpen(false);
                          }}
                        >
                          <input
                            type="radio"
                            id={`${month.short}-${year}`}
                            name="monthItem"
                            value={`${month.short}-${year}`}
                            defaultChecked={isDefaultSelectedMonth}
                          />
                          <label
                            htmlFor={`${month.short}-${year}`}
                          >{`${month.short}`}</label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <SelectInput
            containerClassName={`select-input-field ${styles.inputContainer}`}
            className={styles.input}
            label={'Duration'}
            labelClassName={styles.label}
            value={params.tripDuration}
            options={tripDurations}
            onChange={onChange('tripDuration')}
            onBlur={() => setDirty({ ...dirty, tripDuration: true })}
            status={dirty.tripDuration ? validity.tripDuration : undefined}
          />
        </div>
      </div>

      <div className={`controls-container ${styles.buttonContainer}`}>
        <Button
          type={'button'}
          className={styles.button}
          onClick={e => {
            e.preventDefault();
            setParams(defaultParams);
            setDepartureLocation(undefined);
            setArriveLocation(undefined);
            setShowResults('');
            setSearchResultsFound(false);
          }}
        >
          Clear all filters
        </Button>

        <Button
          className={styles.button}
          disabled={!searchResultsFound}
          onClick={() => {
            const newValidity = validateParams(params);

            if (!isValid(newValidity)) {
              setValidity(newValidity);

              return markAllDirty();
            }

            search(params);
          }}
        >
          <Icon className={'mr-2'} icon={'search'} />
          <span>Search cruises</span>
        </Button>

        <span
          className={clsx(`total-results ${styles.totalResults}`, {
            'd-none': showResults == '',
          })}
        >
          {showResults}
        </span>
      </div>
    </div>
  );
};

export default CruiseForm;
